import type { DynamicSocketDefinition } from "./socketDefinition";

/**
 * Marker suffix carried by every dynamic socket id generated under
 * `__socketIdVersion: 2`.
 *
 * Socket ids do quadruple duty: socket identity on a node, the key the engine
 * reads the upstream payload with (`sourceOutputData[edge.sourceSocketId]` in
 * `graphTraversal`), a shared namespace with orchestration ids like
 * `__flow_control_in__`, and the join key inside stored socket expressions.
 * Dynamic ids come from user data — CSV column names, switch case labels,
 * sandbox variable keys — so without a marker a user-named socket can land on
 * top of one of the step's own fixed sockets, and the collision is silent.
 *
 * `::` is the discriminator: a fixed socket id must never contain it (asserted
 * by `fixedSocketIds.test.ts`), so the two namespaces cannot intersect. That is
 * one rule in place of the per-family prefixes (`col_`, `field:`) and
 * reserved-id blocklists that grew up around individual steps.
 */
export const DYNAMIC_SOCKET_ID_MARKER = "::dynamic";

/**
 * `WorkflowEdge.sourceSocketId` / `targetSocketId` are `STRING(100)`, so an id
 * longer than this cannot be persisted on an edge at all.
 */
export const SOCKET_ID_MAX_LENGTH = 100;

/** True when `id` was generated under the marker scheme. */
export function isDynamicSocketId(id: string): boolean {
  return id.endsWith(DYNAMIC_SOCKET_ID_MARKER);
}

/**
 * Build the marked socket id for a user-supplied `name`.
 *
 * The name is truncated so the marker always survives — the marker is what
 * makes the id safe, and a truncation that dropped it would produce an id that
 * looks fixed and can collide.
 */
export function dynamicSocketId(name: string): string {
  const budget = SOCKET_ID_MAX_LENGTH - DYNAMIC_SOCKET_ID_MARKER.length;
  if (name.length <= budget) {
    return `${name}${DYNAMIC_SOCKET_ID_MARKER}`;
  }

  // Plain truncation would map every name sharing this prefix onto one id, and
  // `buildDynamicSockets` would then refuse all but the first — silently
  // dropping a configured socket. The digest is of the WHOLE name, so two long
  // names differing only past the cut still get distinct ids. Only over-budget
  // names carry it, so ordinary ids stay readable.
  const digest = nameDigest(name);
  const head = sliceCodeUnitsWholeCharacters(name, budget - digest.length - 1);
  return `${head}_${digest}${DYNAMIC_SOCKET_ID_MARKER}`;
}

/**
 * Take at most `budget` UTF-16 code units from `name` without splitting a
 * character.
 *
 * A plain `.slice()` counts code units, so a cut landing inside an astral
 * character — an emoji in a switch case label, say, now that version 2 accepts
 * free text — leaves a lone surrogate. That is not encodable as UTF-8, so it
 * cannot survive a round trip through the database: the id persisted on the
 * edge would differ from the one the handler re-derives, and the branch would
 * stop routing with nothing raised.
 *
 * The budget stays in code units because the limit it serves is the
 * `STRING(100)` edge column.
 */
function sliceCodeUnitsWholeCharacters(name: string, budget: number): string {
  let out = "";
  // Iterating a string yields whole code points, never half a surrogate pair.
  for (const character of name) {
    if (out.length + character.length > budget) break;
    out += character;
  }
  return out;
}

/**
 * Short deterministic digest of a socket name (FNV-1a, base36).
 *
 * Hand-rolled rather than `crypto`: this runs in the editor as well as the
 * backend, and both must derive the same id from the same name. The output is
 * PERSISTED on edges and inside `(output ...)` expressions, so this function's
 * result for a given input can never change once shipped.
 */
function nameDigest(name: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index);
    // >>> 0 keeps it an unsigned 32-bit value; Math.imul avoids the precision
    // loss a plain `*` would hit on the 16777619 multiply.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(7, "0");
}

/**
 * Socket-id scheme in force for a step, read from its persisted config.
 *
 * Version 1 (anything without the key) derives ids as the bare user name, which
 * is what every workflow saved before the marker shipped has on its edges.
 * Version 2 applies the marker. The version is stamped on a step when it is
 * created and never changed afterwards, because changing it would rewrite the
 * step's socket ids while its edges, its `(output ...)` expressions and its
 * `__static_inputs` keys still referenced the old ones.
 */
export const SOCKET_ID_VERSION_CONFIG_KEY = "__socketIdVersion";

/** The scheme new steps are stamped with. */
export const CURRENT_SOCKET_ID_VERSION = 2;

export function socketIdVersion(config: Record<string, unknown>): number {
  const raw = config[SOCKET_ID_VERSION_CONFIG_KEY];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
}

/**
 * Step types whose handler reads and writes its payload by socket id, and which
 * may therefore be stamped with version 2 when a new step is created.
 *
 * A step type belongs here only once its handler is converted. Marking a
 * socket `sku::dynamic` while the handler still writes `output[column.name]`
 * would leave the engine looking up `sku::dynamic` in a payload keyed `sku` —
 * it would read `undefined` and the branch would die silently. So enabling a
 * step type here and converting its handler are one change, never two.
 *
 * Only new steps consult this. A step already stamped keeps its version
 * forever: re-deriving its ids would rewrite them while its edges, its
 * `(output ...)` expressions and its `__static_inputs` keys still referenced
 * the old ones.
 */
export const SOCKET_ID_V2_STEP_TYPES: ReadonlySet<string> = new Set<string>([
  // switchHandler keys its output by socket id via resolveSwitchCaseSockets.
  "switch",
  // statefulTableLookupRowHandler keys its output by socket id via
  // resolveLookupRowColumnSockets.
  "stateful_table_lookup_row",
  // statefulTableReadFrameHandler reads filters and writes per-column arrays by
  // socket id via resolveReadFrameFilter/ColumnOutputSockets.
  "stateful_table_read_frame",
  // csvTransformV4Handler reads dynamic inputs by socket id via
  // resolveCsvTransformV4InputSockets, then exposes them to formulas under
  // their configured name.
  "csv_transform_v4",
  // entityResolverHandler reads data inputs by socket id via
  // resolveEntityResolverInputSockets. Its outputs already carried explicit
  // socketIds in config. NOTE `web_browser` shares the input generator but is
  // deliberately NOT here — its handler still reads by key.
  "entity_resolver",
  // splitDocumentV2Handler keys its per-classification outputs by socket id via
  // resolveSplitDocumentV2OutputSockets.
  "split_document_v2",
  // identifyCustomerHandler keys its per-route outputs by socket id via
  // resolveIdentifyCustomerOutputSockets.
  "identify_customer",
  "identify_supplier",
  // classifyEmailHandler keys its per-category outputs by socket id via
  // resolveClassifyEmailOutputSockets.
  "classify_email",
  // codeSandboxHandler remaps socket id <-> declared key in both directions
  // around the sandbox call, via resolveCodeSandbox{Input,Output}Sockets.
  "code_sandbox",
  // matchCustomerWithEvidenceHandler reads its evidence by socket id via
  // resolveMatchCustomerWithEvidenceInputSockets.
  "match_customer_with_evidence",
]);

export function supportsSocketIdV2(stepType: string): boolean {
  return SOCKET_ID_V2_STEP_TYPES.has(stepType);
}

/**
 * The socket-id version a generator must actually use for `stepType`.
 *
 * `socketIdVersion` reads the stamp off config, and config arrives from the
 * client on every save and from imported templates — so the stamp alone is a
 * claim, not authorization. A step of a type whose handler still reads and
 * writes bare configured names would, if that claim were honored, get marked
 * ids on the canvas and nowhere to deliver them: inputs arrive undefined,
 * outputs land on keys no edge references, and nothing raises.
 *
 * The opt-in set is the authority. A stamp on an unconverted type is ignored
 * rather than rejected, because refusing it would break the save of a workflow
 * whose config merely carries a stale key.
 */
export function effectiveSocketIdVersion(
  stepType: string,
  config: Record<string, unknown>,
): number {
  return supportsSocketIdV2(stepType) ? socketIdVersion(config) : 1;
}

/**
 * Force an EXISTING step's socket-id stamp back to what was persisted for it.
 *
 * The stamp is written once, when the step is created, and must never move
 * afterwards: it decides whether the step's sockets are `sku` or
 * `sku::dynamic`, while its edges, its stored `(output ...)` expressions and
 * its `__static_inputs` keys still name the ids derived under the old value.
 * Nothing about a draft save enforces that on its own — `updateWorkflowDefinition`
 * persists client-sent config wholesale, so a crafted or stale payload could
 * add, change or drop the key and silently re-derive every dynamic socket id on
 * that step.
 *
 * Both directions matter. Dropping the stamp from a version 2 step reverts its
 * ids to bare; adding one to a legacy step marks ids that every existing edge
 * still references bare. Either way the edges survive the save pointing at ids
 * no socket has any more.
 *
 * Call this ONLY for steps that already exist. A newly created step has no
 * persisted value to preserve, and its stamp comes from
 * `newStepSocketIdVersionConfig`, which is gated on the opt-in set.
 */
export function preserveSocketIdVersion(
  config: Record<string, unknown>,
  persistedVersion: unknown,
): Record<string, unknown> {
  const { [SOCKET_ID_VERSION_CONFIG_KEY]: _incoming, ...rest } = config;
  return typeof persistedVersion === "number" &&
    Number.isFinite(persistedVersion)
    ? { ...rest, [SOCKET_ID_VERSION_CONFIG_KEY]: persistedVersion }
    : rest;
}

/**
 * The `__socketIdVersion` entry to merge into a newly created step's config, or
 * nothing when the step type is not converted yet. Spread into the config at
 * every step-creation site so the stamp cannot be applied inconsistently.
 */
export function newStepSocketIdVersionConfig(
  stepType: string,
): Record<string, number> {
  return supportsSocketIdV2(stepType)
    ? { [SOCKET_ID_VERSION_CONFIG_KEY]: CURRENT_SOCKET_ID_VERSION }
    : {};
}

/** A user-named entry that becomes one dynamic socket. */
export interface DynamicSocketEntry {
  /** The user-supplied name this socket's id derives from. */
  name: string;
  /** Everything about the socket except its id. */
  socket: Omit<DynamicSocketDefinition, "id">;
}

/** A dynamic socket that could not be emitted, and why. */
export interface RefusedDynamicSocket {
  name: string;
  reason: "reserved" | "duplicate";
}

export interface BuildDynamicSocketsResult {
  sockets: DynamicSocketDefinition[];
  refused: RefusedDynamicSocket[];
  /**
   * The socket id assigned to each entry, positionally, or `null` where the
   * entry was refused.
   *
   * Handlers need this. A handler receives `(input, config, context)` — never
   * its sockets — so it has to derive the same ids the generator did in order
   * to key its payload with them. Reading them off the same call that built the
   * sockets is what keeps the two from drifting; re-deriving them by hand is
   * how a handler ends up writing `output[label]` for a socket the canvas calls
   * `label::dynamic`, which the engine then reads as undefined.
   */
  assignedIds: Array<string | null>;
}

/**
 * Build one socket per user-named entry, guaranteeing no two of them share an
 * id and none collides with a `reserved` id.
 *
 * Returns only the dynamic sockets; the caller composes them with its fixed
 * ones. That is deliberate — each family orders the two groups differently
 * (`switch` emits cases then `default`, `identify_customer` emits `top_pick`
 * then routes then `others`, `use_data_mapping` emits its fixed pair first),
 * and the emitted order is persisted on the step, so a helper that imposed one
 * order would rewrite existing nodes' socket arrays.
 *
 * This generalizes the `seenIds` guard PR #5570 added to `csv_transform_v4`,
 * which was the only family that had one. Two distinct collisions are possible
 * and both are handled:
 *
 *  - **fixed vs dynamic** — a column named `rows`, a case labelled `default`.
 *    Under version 2 the marker makes this impossible, so nothing is refused.
 *  - **dynamic vs dynamic** — two cases sharing a label, two dynamic inputs
 *    sharing a name. Both derive the same id, so the marker alone cannot
 *    separate them.
 *
 * In either case, on a version 1 step, `markedFallback` decides what happens to
 * the losing entry: it takes the marked id, or it is refused. See that option.
 *
 * Refusals are returned rather than thrown so each caller decides: a config
 * schema can surface them as validation errors, while a socket generator
 * running on already-persisted config must not explode.
 */
export function buildDynamicSockets({
  reserved = [],
  entries,
  version,
  markedFallback = false,
}: {
  reserved?: readonly string[];
  entries: DynamicSocketEntry[];
  version: number;
  /**
   * On a version 1 step, give an entry that collides with a RESERVED id the
   * marked id instead of refusing it.
   *
   * Such an entry never had a working id: the fixed socket owned the name, and
   * the handler wrote that key last, so the entry's branch was dead. Handing it
   * `name::dynamic` takes nothing away — no version 1 step can have generated a
   * marked id, so no saved edge or `(output ...)` expression references it, and
   * the branch goes from unreachable to working while the fixed socket keeps
   * the semantics its edges were wired for.
   *
   * Deliberately NOT applied to an entry colliding with another ENTRY. Those
   * two shared one real socket, and an edge on it fired for whichever of them
   * produced a value; moving the loser to a marked id would silently stop that
   * branch. Those are aliased onto the shared id instead.
   *
   * Only safe where the handler resolves its payload keys through this helper.
   * A generator that emitted a marked id for a step type whose handler still
   * writes `output[name]` would put a socket on the canvas that always reads
   * undefined — a handle that looks alive and is not. So this tracks the same
   * per-step-type gate as SOCKET_ID_V2_STEP_TYPES.
   */
  markedFallback?: boolean;
}): BuildDynamicSocketsResult {
  const reservedIds = new Set(reserved);
  const seenIds = new Set(reservedIds);
  const sockets: DynamicSocketDefinition[] = [];
  const refused: RefusedDynamicSocket[] = [];
  const assignedIds: Array<string | null> = [];
  const marked = version >= CURRENT_SOCKET_ID_VERSION;
  // On version 1 an entry's id IS its name, so another entry may already be
  // named `foo::dynamic` — exactly the id the fallback would mint for `foo`.
  // Taking it would hand `foo` the id that entry has been using all along and
  // leave that one aliased onto metadata describing a different column.
  const bareIdsInUse = marked
    ? new Set<string>()
    : new Set(entries.map((entry) => entry.name));

  for (const entry of entries) {
    let id = marked ? dynamicSocketId(entry.name) : entry.name;

    // Only a collision with a RESERVED id earns the marked fallback. A
    // collision with another entry must not: on version 1 the two entries
    // shared one socket, and any saved edge on it carried whichever of them
    // produced a value. Minting a second socket for the loser would move its
    // output to an id no edge references, silently killing that branch.
    if (seenIds.has(id) && !marked && markedFallback && reservedIds.has(id)) {
      const fallbackId = dynamicSocketId(entry.name);
      // Refuse the entry rather than take an id another entry already owns.
      if (!bareIdsInUse.has(fallbackId)) {
        id = fallbackId;
      }
    }

    if (seenIds.has(id)) {
      const collidesWithReserved = reservedIds.has(id);
      refused.push({
        name: entry.name,
        reason: collidesWithReserved ? "reserved" : "duplicate",
      });
      // A version-1 duplicate is ALIASED onto the id it collided with rather
      // than dropped, so the handler still emits its value on the socket the
      // edge points at — the shared-socket behavior these configs were saved
      // under. No second socket is emitted, so the ids stay unique.
      // Under version 2 the ids are already distinct, so a collision here is a
      // genuinely unroutable duplicate and stays refused.
      assignedIds.push(!marked && !collidesWithReserved ? id : null);
      continue;
    }

    seenIds.add(id);
    sockets.push({ id, ...entry.socket });
    assignedIds.push(id);
  }

  return { sockets, refused, assignedIds };
}
